import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { SuccessMessageControl } from "@tlc/ls2pac/app/controls/successMessageControl";
import { useLanguage } from "@tlc/ls2pac/components/Language";
import {
  suspendHold,
  SuspendHoldInfo,
} from "@tlc/ls2pac/components/services/Holds";
import { HostSystemItemView } from "@tlc/ls2pac/dto/HostSystemItemView";
import { Dayjs } from "dayjs";
import React from "react";
import { useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";

export interface SuspendHoldsDialogProps {
  open: boolean;
  holds: HostSystemItemView[];
  onSuspend: (suspended: SuspendHoldInfo[]) => void;
  onClose: () => void;
}
export interface SuspendHoldsFormInput {
  suspendUntil: Dayjs;
}
export const SuspendHoldsDialog: React.FC<SuspendHoldsDialogProps> = ({
  open,
  onSuspend,
  onClose,
  holds,
}) => {
  const { translate } = useLanguage();
  const {
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<SuspendHoldsFormInput>({
    mode: "onTouched",
    defaultValues: {
      suspendUntil: null,
    },
  });
  const [suspendError, setSuspendError] = useState<string | null>(null);

  const onSubmit: SubmitHandler<SuspendHoldsFormInput> = async (data) => {
    const { suspendUntil } = data;
    setSuspendError(null);

    try {
      const { suspendHoldInfos } = await suspendHold(
        holds,
        suspendUntil.toDate()
      );
      const errors = suspendHoldInfos.filter((hold) => hold.success === false);
      const successes = suspendHoldInfos.filter(
        (hold) => hold.success === true
      );

      if (successes.length > 0) {
        SuccessMessageControl.show(
          `${successes.length} ${translate("suspendedMessage")}`
        );
        onSuspend(successes);
        if (errors.length === 0) {
          onClose();
        }
      }

      if (errors.length > 0) {
        setSuspendError(
          `${errors.length} ${translate("suspendHoldFailureMessage")}`
        );
      }
    } catch (e) {
      setSuspendError(translate("suspendHoldFailureMessage"));
    }
  };

  const handleExited = () => {
    reset();
    setSuspendError(null);
  };

  return (
    <Dialog open={open} onClose={onClose} onTransitionExited={handleExited}>
      {!suspendError && (
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <DialogContent>
            <Controller
              name="suspendUntil"
              control={control}
              rules={{ required: translate("requiredFieldError") }}
              render={({ field: { onChange, value, onBlur } }) => {
                return (
                  <DatePicker
                    autoFocus
                    slotProps={{
                      textField: {
                        error: !!errors.suspendUntil,
                        helperText: errors.suspendUntil?.message || " ",
                        required: true,
                        onBlur,
                      },
                    }}
                    onChange={onChange}
                    value={value}
                    disablePast
                    label={translate("suspend")}
                  ></DatePicker>
                );
              }}
            ></Controller>
          </DialogContent>
          <DialogActions>
            <Button type="button" onClick={onClose}>
              {translate("cancel")}
            </Button>
            <Button type="submit" variant="contained">
              {translate("save")}
            </Button>
          </DialogActions>
        </form>
      )}
      {suspendError && (
        <>
          <DialogContent>
            <Alert severity="error">{suspendError}</Alert>
          </DialogContent>
          <DialogActions>
            <Button type="button" onClick={onClose}>
              {translate("ok")}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};
